import { ANSWER_MAX } from '@mogzo/shared';
import { type FormEvent, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { SiteFooter } from '../../components/SiteFooter';
import {
  Alert,
  AuthLayout,
  Button,
  inputClass,
  PageSpinner,
  SiteHeader,
  TextLink,
} from '../../components/ui';
import { ApiError } from '../../lib/api';
import {
  type ApplicationThread,
  fetchQuestion,
  sendAnswer,
} from '../../lib/developer-applications';
import { CONTACT_EMAIL } from '../../lib/legal';

type State =
  | { status: 'loading' }
  | { status: 'ready'; thread: ApplicationThread }
  | { status: 'answered' }
  | { status: 'failed'; message: string };

function when(date: string) {
  return new Date(date).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function failure(error: unknown): State {
  return {
    status: 'failed',
    message:
      error instanceof ApiError && error.code === 'invalid_token'
        ? error.message
        : 'We couldn’t load this just now. Try the link again in a moment.',
  };
}

/** Landing page of the "A question about your MOGZO application" email. */
export function AnswerDeveloperQuestion() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<State>({ status: 'loading' });
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setState({ status: 'failed', message: 'This link is missing its code.' });
      return;
    }
    let active = true;
    fetchQuestion(token)
      .then((thread) => active && setState({ status: 'ready', thread }))
      .catch((failed) => active && setState(failure(failed)));
    return () => {
      active = false;
    };
  }, [token]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!answer.trim()) return setError('Write your answer first.');
    setBusy(true);
    setError(null);
    try {
      await sendAnswer(token, answer.trim());
      setState({ status: 'answered' });
    } catch (failed) {
      setBusy(false);
      if (failed instanceof ApiError && failed.code === 'invalid_token') setState(failure(failed));
      else setError('We couldn’t send that. Try again in a moment.');
    }
  }

  if (state.status === 'loading') return <PageSpinner />;
  if (state.status === 'failed') {
    return (
      <AuthLayout title="This link didn’t work">
        <p className="text-ink">{state.message}</p>
        <p className="mt-4 text-sm text-muted">
          Need help? Write to{' '}
          <a className="font-semibold underline" href={`mailto:${CONTACT_EMAIL}`}>
            {CONTACT_EMAIL}
          </a>
          .
        </p>
      </AuthLayout>
    );
  }
  if (state.status === 'answered') {
    return (
      <AuthLayout title="Answer sent">
        <p className="text-ink">
          Thanks. Your answer is back in our review, and we’ll email you when we’ve decided.
        </p>
        <p className="mt-4 text-sm text-muted">
          <TextLink to="/">Back to MOGZO</TextLink>
        </p>
      </AuthLayout>
    );
  }

  const { thread } = state;
  const expires = new Date(thread.expiresAt).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  return (
    <div className="flex min-h-dvh flex-col">
      <SiteHeader />
      <main className="mx-auto w-full max-w-2xl flex-1 space-y-5 px-4 py-10 sm:px-6 sm:py-12">
        <div>
          <p className="text-xs font-bold tracking-[0.25em] text-muted uppercase">
            Your developer application
          </p>
          <h1 className="mt-3 text-3xl font-bold text-ink">We have a question</h1>
          <p className="mt-2 text-muted">
            Hi {thread.name.split(/\s+/)[0]}. Your answer goes straight back into our review.
          </p>
        </div>

        <section
          aria-labelledby="thread-heading"
          className="space-y-4 rounded-2xl border border-line bg-card p-6 sm:p-7"
        >
          <h2 id="thread-heading" className="text-base font-bold text-ink">
            {thread.messages.length > 1
              ? 'Your conversation with the MOGZO team'
              : 'The MOGZO team asked'}
          </h2>
          {thread.messages.map((message) => (
            <div key={message.createdAt + message.author}>
              <p className="mb-1.5 font-mono text-xs text-muted">
                {message.author === 'staff' ? 'MOGZO asked' : 'You answered'} ·{' '}
                {when(message.createdAt)}
              </p>
              <blockquote
                className={`whitespace-pre-wrap rounded-xl px-4 py-3.5 leading-relaxed text-ink ${message.author === 'staff' ? 'bg-field' : 'border border-line'}`}
              >
                {message.body}
              </blockquote>
            </div>
          ))}
        </section>

        <form
          onSubmit={onSubmit}
          className="space-y-3 rounded-2xl border border-line bg-card p-6 sm:p-7"
        >
          {error && <Alert>{error}</Alert>}
          <label htmlFor="answer" className="block font-bold text-ink">
            Your answer
          </label>
          <textarea
            id="answer"
            rows={6}
            maxLength={ANSWER_MAX}
            className={`${inputClass} resize-y`}
            value={answer}
            onChange={(event) => setAnswer(event.target.value)}
          />
          <p className="text-right font-mono text-xs text-muted" aria-live="polite">
            {answer.length.toLocaleString('en')} / {ANSWER_MAX.toLocaleString('en')}
          </p>
          <Button busy={busy}>Send answer</Button>
          <p className="text-center text-xs text-muted">
            This link only works for your application, until {expires}.
          </p>
        </form>
      </main>
      <SiteFooter />
    </div>
  );
}
